import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import './ImageGallery.css';

const ImageGallery = ({ images }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [loadedImages, setLoadedImages] = useState(new Set());
  const intervalRef = useRef(null);
  const imageRefs = useRef({});

  // Helper function to get correct image path
  const getImagePath = (imagePath) => {
    // If it's already a full URL, return as is
    if (typeof imagePath === 'string' && (imagePath.startsWith('http://') || imagePath.startsWith('https://'))) {
      return imagePath;
    }
    // If it starts with /, it's from public folder - use as is
    if (typeof imagePath === 'string' && imagePath.startsWith('/')) {
      return imagePath;
    }
    // For imported images (webpack processed), use directly
    if (typeof imagePath === 'string') {
      return imagePath;
    }
    // If it's a webpack module object, try to get the default export
    return imagePath;
  };

  // Preload all images
  useEffect(() => {
    if (!images || images.length === 0) return;

    const preloadImages = () => {
      const imagePromises = images.map((image, index) => {
        return new Promise((resolve, reject) => {
          const img = new Image();
          const imageSrc = getImagePath(image);
          
          img.onload = () => {
            setLoadedImages(prev => {
              const newSet = new Set(prev);
              newSet.add(index);
              return newSet;
            });
            resolve();
          };
          
          img.onerror = () => {
            console.error('Failed to preload image:', image, 'Path:', imageSrc);
            // Still resolve to not block the gallery
            resolve();
          };
          
          img.src = imageSrc;
        });
      });

      Promise.all(imagePromises).then(() => {
        setImagesLoaded(true);
      });
    };

    preloadImages();
  }, [images]);

  // Timer'ı başlat ve sıfırla
  const resetTimer = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    if (images.length > 1 && imagesLoaded) {
      intervalRef.current = setInterval(() => {
        setCurrentImageIndex((prevIndex) => (prevIndex + 1) % images.length);
      }, 3000);
    }
  };

  // İlk yüklemede timer'ı başlat
  useEffect(() => {
    if (imagesLoaded) {
      resetTimer();
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [images.length, imagesLoaded]);

  const goToPrevious = () => {
    setCurrentImageIndex((prevIndex) => (prevIndex - 1 + images.length) % images.length);
    resetTimer(); // Timer'ı sıfırla
  };

  const goToNext = () => {
    setCurrentImageIndex((prevIndex) => (prevIndex + 1) % images.length);
    resetTimer(); // Timer'ı sıfırla
  };

  const goToImage = (index) => {
    setCurrentImageIndex(index);
    resetTimer(); // Timer'ı sıfırla
  };

  if (!images || images.length === 0) {
    return null;
  }

  // Get the current image path
  const currentImage = images[currentImageIndex];
  const imageSrc = getImagePath(currentImage);

  return (
    <div className="gallery-container">
      <img 
        src={imageSrc} 
        alt={`Galeri Fotoğrafı ${currentImageIndex + 1}`} 
        className="gallery-image"
        loading="eager"
        onLoad={() => {
          // Ensure image is visible when loaded
          if (imageRefs.current[currentImageIndex]) {
            imageRefs.current[currentImageIndex].style.display = 'block';
          }
        }}
        onError={(e) => {
          console.error('Image failed to load:', currentImage, 'Resolved path:', imageSrc);
          e.target.style.display = 'none';
        }}
        ref={(el) => {
          if (el) {
            imageRefs.current[currentImageIndex] = el;
          }
        }}
      />
      {images.length > 1 && (
        <>
          <button onClick={goToPrevious} className="gallery-button gallery-button-left">
            <FontAwesomeIcon icon={faChevronLeft} />
          </button>
          <button onClick={goToNext} className="gallery-button gallery-button-right">
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
          <div className="gallery-dots">
            {images.map((_, index) => (
              <span
                key={index}
                className={`gallery-dot ${index === currentImageIndex ? 'active' : ''}`}
                onClick={() => goToImage(index)}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default ImageGallery;
